import { Memoize } from 'lodash-decorators';
import { Field } from './Field';
import { HierarchyMember } from './Member';
import {
  METRICS,
  PERCENT_TO_TOTAL_VERSION,
  PERCENT_TO_GRAND_TOTAL_VERSION,
  DEFAULT_PERCENT_FORMAT,
  SINGLE_VERSION,
} from 'src/utils/Domain/Constants';
import { PERCENTAGE_VARTYPE } from 'src/services/Pivot.types';
import { isNil } from 'lodash';

export default class Intersection {
  protected fields: Field[];
  protected measureField: Field | undefined = undefined;
  // When the grid is driven by filters, the metric/revision that dictate formatting can live
  // outside the grid. These carry that out-of-grid selection in so we can fall back to it.
  protected maybeFilterMetric: Field | undefined;
  protected maybeFilterRevision: Field | undefined;

  constructor(fields: Field[], maybeFilterMetric?: Field, maybeFilterRevision?: Field) {
    this.fields = [...fields];
    this.maybeFilterMetric = maybeFilterMetric;
    this.maybeFilterRevision = maybeFilterRevision;

    for (const field of this.fields) {
      if (field.member.level.dimension.id === METRICS) {
        this.measureField = field;
        break;
      }
    }
  }

  public hasMeasure(): boolean {
    return !!this.measureField;
  }
  public maybeGetRevisionFormatString(): string | undefined {
    // there are three ways that a intersection can be alternately formatted, and we need to
    // extract that formatting from a different place on the metric member
    const hasPctVarType = this.fields.findIndex((f) => f.member.varType === PERCENTAGE_VARTYPE) >= 0;
    const hasPctTotal = this.fields.findIndex((f) => f.member.type === PERCENT_TO_TOTAL_VERSION) >= 0;
    const hasPctGrandTotal = this.fields.findIndex((f) => f.member.type === PERCENT_TO_GRAND_TOTAL_VERSION) >= 0;

    if (hasPctVarType) {
      return this.measureField?.member.format?.variancePercentGrid || DEFAULT_PERCENT_FORMAT;
    }
    if (hasPctTotal) {
      return this.measureField?.member.format?.percentTotalGrid || DEFAULT_PERCENT_FORMAT;
    }
    if (hasPctGrandTotal) {
      return this.measureField?.member.format?.percentGrandTotalGrid || DEFAULT_PERCENT_FORMAT;
    }
    if (this.maybeFilterRevision && !this.maybeFilterRevision.member.varType) {
      if (this.maybeFilterRevision.member.type === SINGLE_VERSION) {
        return;
      }
      // TODO actually need to do a double lookup here
      if (this.maybeFilterRevision.member.varType === PERCENTAGE_VARTYPE) {
        return this.maybeFilterRevision?.member.format?.variancePercentGrid || DEFAULT_PERCENT_FORMAT;
      }
      if (this.maybeFilterRevision.member.type === PERCENT_TO_TOTAL_VERSION) {
        return this.maybeFilterRevision?.member.format?.percentTotalGrid || DEFAULT_PERCENT_FORMAT;
      }
      if (this.maybeFilterRevision.member.type === PERCENT_TO_GRAND_TOTAL_VERSION) {
        return this.maybeFilterRevision?.member.format?.percentGrandTotalGrid || DEFAULT_PERCENT_FORMAT;
      }
    }
  }

  public maybeGetRevisionLocation(): 'variancePercentGrid' | 'percentTotalGrid' | 'percentGrandTotalGrid' | undefined {
    // there are three ways that a intersection can be alternately formatted, and we need to
    // extract that formatting from a different place on the metric member
    const hasPctVarType = this.fields.findIndex((f) => f.member.varType === PERCENTAGE_VARTYPE) >= 0;
    const hasPctTotal = this.fields.findIndex((f) => f.member.type === PERCENT_TO_TOTAL_VERSION) >= 0;
    const hasPctGrandTotal = this.fields.findIndex((f) => f.member.type === PERCENT_TO_GRAND_TOTAL_VERSION) >= 0;

    if (hasPctVarType) {
      return 'variancePercentGrid' as const;
    }
    if (hasPctTotal) {
      return 'percentTotalGrid' as const;
    }
    if (hasPctGrandTotal) {
      return 'percentGrandTotalGrid' as const;
    }
    if (this.maybeFilterRevision) {
      // TODO actually need to do a double lookup here
      const hasPctVarType = this.fields.findIndex((f) => f.member.varType === PERCENTAGE_VARTYPE) >= 0;
      const hasPctTotal = this.fields.findIndex((f) => f.member.type === PERCENT_TO_TOTAL_VERSION) >= 0;
      const hasPctGrandTotal = this.fields.findIndex((f) => f.member.type === PERCENT_TO_GRAND_TOTAL_VERSION) >= 0;
    }
  }

  public getFormat(): string | undefined {
    // if it has revision formatting, take that first,
    // otherwise try and get the standard member formatting,
    // fall back to the out-of-grid filter metric
    // when the metric isn't present within the grid,
    // and undefined if neither has it.
    return !isNil(this.maybeGetRevisionFormatString())
      ? this.maybeGetRevisionFormatString()
      : this.getFormatMember()?.format?.standard;
  }

  /**
   * The metric member whose `format` should drive this intersection. Prefer the metric found
   * inside the grid (`measureField`); fall back to the filter metric selected outside the grid.
   */
  private getFormatMember(): HierarchyMember | undefined {
    if (this.measureField) {
      return this.measureField.member;
    }
    return this.maybeFilterMetric?.member;
  }

  public getMeasureField(): Field | undefined {
    return this.measureField ?? this.maybeFilterMetric;
  }

  @Memoize()
  public getFieldIds(): string[] {
    return this.fields.map((field) => `${field.member.level.id}:${field.member.id}`);
  }
  public getFieldListAsPipeString(): string {
    const idLst = this.fields.reduce((prev, currentField) => {
      return (prev += `${currentField.member.id}|`);
    }, '');
    return idLst.substring(0, idLst.length - 1);
  }

  public getField(index: number): Field {
    return this.fields[index];
  }

  public getLastIndex(): Field {
    return this.fields[this.fields.length - 1];
  }

  public getSecondToLastIndex(): Field {
    return this.fields[this.fields.length - 2];
  }

  public getLineIndex(): Field {
    return this.getSecondToLastIndex() || this.getLastIndex();
  }

  public matches(matchFn: (field: Field) => boolean): boolean {
    for (const field of this.fields) {
      if (matchFn(field)) {
        return true;
      }
    }
    return false;
  }

  public toString() {
    return this.fields.map((f) => f.member.name).join(' ✕ ');
  }
}
